import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  getCustomers,
  getSites,
  getHeatPumps,
  loadReport,
  saveReport,
} from "../lib/sync";
import { useAuth } from "../context/AuthContext";
import { getForm } from "../lib/formTemplates";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Progress } from "../components/ui/progress";
import { EntitySelect as SelectField } from "../components/EntitySelect";
import { QuickAddDialog } from "../components/QuickAddDialog";
import BarcodeScanner from "../components/BarcodeScanner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import SyncBadge from "../components/SyncBadge";
import SignaturePad from "../components/SignaturePad";
import PhotoUpload from "../components/PhotoUpload";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Save,
  Loader2,
  Pencil,
  Trash2,
  ScanLine,
} from "lucide-react";
import { toast } from "sonner";

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function ReportForm() {
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const [params] = useSearchParams();
  const { user } = useAuth();

  const [report, setReport] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [pumps, setPumps] = useState([]);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [addType, setAddType] = useState(null);
  const [addForm, setAddForm] = useState({});
  const [addSaving, setAddSaving] = useState(false);
  const [pumpEditId, setPumpEditId] = useState(null);
  const [removePumpOpen, setRemovePumpOpen] = useState(false);
  const initialised = useRef(false);

  const requestedType = params.get("type") || "service_report";
  const formType = report?.form_type || requestedType;
  const formDef = getForm(formType);

  const STEPS = useMemo(
    () => ["Job & Asset", ...formDef.sections.map((s) => s.title), "Sign-off"],
    [formDef]
  );

  // ---- init ----
  useEffect(() => {
    if (initialised.current) return;
    initialised.current = true;
    (async () => {
      const [cs, allSites, allPumps] = await Promise.all([
        getCustomers().catch(() => []),
        getSites().catch(() => []),
        getHeatPumps().catch(() => []),
      ]);
      setCustomers(cs);
      setSites(allSites);
      setPumps(allPumps);

      const dupId = params.get("duplicate");
      if (routeId) {
        const existing = await loadReport(routeId).catch(() => null);
        if (existing) {
          const { _sync, ...r } = existing;
          setReport(r);
          return;
        }
      }
      if (dupId) {
        const src = await loadReport(dupId).catch(() => null);
        if (src) {
          const { _sync, id, share_id, created_at, updated_at, ...rest } = src;
          setReport({
            ...rest,
            id: crypto.randomUUID(),
            status: "draft",
            service_date: todayISO(),
            engineer_id: user?.id || rest.engineer_id,
            engineer_name: user?.name || rest.engineer_name,
            engineer_signature: "",
            customer_signature: "",
            share_id: null,
          });
          toast.success("Report duplicated — update the service date & details");
          return;
        }
      }
      // fresh
      setReport({
        id: crypto.randomUUID(),
        form_type: requestedType,
        customer_id: params.get("customer") || "",
        site_id: params.get("site") || "",
        heat_pump_id: params.get("pump") || "",
        engineer_id: user?.id || "",
        engineer_name: user?.name || "",
        service_date: todayISO(),
        status: "draft",
        data: {},
        photos: [],
        engineer_signature: "",
        customer_signature: "",
        follow_up_actions: "",
        parts_required: "",
        final_status: "",
      });
    })();
  }, [routeId, params, user]);

  // ---- autosave (debounced) ----
  const autoSaveRef = useRef(null);
  const queueAutoSave = useCallback((next) => {
    if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    autoSaveRef.current = setTimeout(() => {
      saveReport(next);
    }, 1200);
  }, []);

  const update = (patch) => {
    setReport((prev) => {
      const next = { ...prev, ...patch };
      queueAutoSave(next);
      return next;
    });
  };
  const updateData = (key, value) =>
    setReport((prev) => {
      const next = { ...prev, data: { ...prev.data, [key]: value } };
      queueAutoSave(next);
      return next;
    });

  const saveDraft = async () => {
    if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    setSaving(true);
    await saveReport({ ...report, status: "draft" });
    setSaving(false);
    toast.success("Draft saved");
  };

  const submit = async () => {
    if (!report.customer_id || !report.site_id || !report.heat_pump_id)
      return toast.error("Select customer, site and heat pump first");
    if (!report.engineer_signature)
      return toast.error("Engineer signature is required to complete");
    if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    setSaving(true);
    const completed = { ...report, status: "completed" };
    setReport(completed);
    await saveReport(completed);
    setSaving(false);
    toast.success("Report completed");
    navigate(`/report/${report.id}/view`);
  };

  const saveAdd = async () => {
    const t = addType;
    if (t === "customer" && !addForm.name) return toast.error("Customer name is required");
    if (t === "site" && !addForm.name) return toast.error("Site name is required");
    setAddSaving(true);
    try {
      if (t === "customer") {
        const { data } = await api.post("/customers", addForm);
        setCustomers(await getCustomers().catch(() => []));
        update({ customer_id: data.id, site_id: "", heat_pump_id: "" });
        toast.success("Customer added");
      } else if (t === "site") {
        const { data } = await api.post("/sites", { ...addForm, customer_id: report.customer_id });
        setSites(await getSites().catch(() => []));
        update({ site_id: data.id, heat_pump_id: "" });
        toast.success("Site added");
      } else if (t === "pump") {
        if (pumpEditId) {
          const { data } = await api.put(`/heatpumps/${pumpEditId}`, {
            ...addForm,
            customer_id: report.customer_id,
            site_id: report.site_id,
          });
          setPumps(await getHeatPumps().catch(() => []));
          update({ heat_pump_id: data.id });
          toast.success("Heat pump updated");
        } else {
          const { data } = await api.post("/heatpumps", {
            ...addForm,
            customer_id: report.customer_id,
            site_id: report.site_id,
          });
          setPumps(await getHeatPumps().catch(() => []));
          update({ heat_pump_id: data.id });
          toast.success("Heat pump added");
        }
      }
      setAddType(null);
      setAddForm({});
      setPumpEditId(null);
    } catch (e) {
      toast.error("Could not save — are you online?");
    } finally {
      setAddSaving(false);
    }
  };

  const openEditPump = () => {
    const p = pumps.find((x) => x.id === report.heat_pump_id);
    if (!p) return;
    setAddForm({ ...p });
    setPumpEditId(p.id);
    setAddType("pump");
  };

  const removePump = async () => {
    setRemovePumpOpen(false);
    const id = report.heat_pump_id;
    try {
      await api.delete(`/heatpumps/${id}`);
      setPumps(await getHeatPumps().catch(() => []));
      update({ heat_pump_id: "" });
      toast.success("Heat pump removed");
    } catch (e) {
      toast.error("Could not remove heat pump");
    }
  };


  if (!report) return <p className="py-10 text-center text-slate-400">Loading…</p>;

  const filteredSites = sites.filter((s) => s.customer_id === report.customer_id);
  const filteredPumps = pumps.filter(
    (p) => p.site_id === report.site_id || (!report.site_id && p.customer_id === report.customer_id)
  );
  const isLast = step === STEPS.length - 1;
  const progress = Math.round(((step + 1) / STEPS.length) * 100);

  // Step 1 (Job & Asset) requires customer, site and heat pump before proceeding.
  const step0Complete = !!(report.customer_id && report.site_id && report.heat_pump_id);
  const canProceed = step !== 0 || step0Complete;

  const goNext = () => {
    if (!canProceed) return;
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };

  return (
    <div className="pb-28">
      {/* header */}
      <div className="mb-4 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm font-semibold text-slate-500" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4" /> Exit
        </button>
        <SyncBadge />
      </div>

      <div className="mb-1 flex items-center justify-between">
        <h1 className="font-heading text-xl font-extrabold text-slate-900">
          {STEPS[step]}
        </h1>
        <span className="text-xs font-semibold text-slate-400">
          Step {step + 1} of {STEPS.length}
        </span>
      </div>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary" data-testid="form-type-label">
        {formDef.label}
      </p>
      <Progress value={progress} className="mb-5 h-2" data-testid="form-progress" />

      {/* step 0: job & asset */}
      {step === 0 && (
        <div className="space-y-4">
          <SelectField
            label="Customer"
            value={report.customer_id}
            onChange={(v) => update({ customer_id: v, site_id: "", heat_pump_id: "" })}
            options={customers.map((c) => ({ value: c.id, label: c.name }))}
            testid="select-customer"
            onAdd={() => {
              setAddForm({});
              setAddType("customer");
            }}
            addTestid="add-customer-inline"
          />
          <SelectField
            label="Site"
            value={report.site_id}
            onChange={(v) => update({ site_id: v, heat_pump_id: "" })}
            options={filteredSites.map((s) => ({ value: s.id, label: s.name }))}
            disabled={!report.customer_id}
            testid="select-site"
            onAdd={() => {
              setAddForm({});
              setAddType("site");
            }}
            addTestid="add-site-inline"
          />
          <SelectField
            label="Heat pump"
            value={report.heat_pump_id}
            onChange={(v) => update({ heat_pump_id: v })}
            options={filteredPumps.map((p) => ({ value: p.id, label: `${p.manufacturer} ${p.model} (${p.serial_number})` }))}
            disabled={!report.site_id}
            testid="select-pump"
            onAdd={() => {
              setAddForm({});
              setAddType("pump");
            }}
            addTestid="add-pump-inline"
          />
          {report.heat_pump_id && (
            <div className="-mt-1 flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={openEditPump}
                data-testid="edit-pump-btn"
              >
                <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit heat pump
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-red-600"
                onClick={() => setRemovePumpOpen(true)}
                data-testid="remove-pump-btn"
              >
                <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Remove
              </Button>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Service date</Label>
              <Input type="date" value={report.service_date || ""} onChange={(e) => update({ service_date: e.target.value })} className="h-12" data-testid="service-date" />
            </div>
            <div className="space-y-1.5">
              <Label>Engineer</Label>
              <Input value={report.engineer_name || ""} onChange={(e) => update({ engineer_name: e.target.value })} className="h-12" data-testid="engineer-name" />
            </div>
          </div>
          <PumpSummary pump={pumps.find((p) => p.id === report.heat_pump_id)} />
          {!step0Complete && (
            <p
              className="rounded-lg bg-amber-50 px-3 py-2.5 text-sm font-medium text-amber-700"
              data-testid="step0-validation"
            >
              Select a customer, site and heat pump to continue.
            </p>
          )}
        </div>
      )}

      {/* template section steps */}
      {step > 0 && step <= formDef.sections.length && (
        <SectionFields
          section={formDef.sections[step - 1]}
          data={report.data}
          updateData={updateData}
        />
      )}

      {/* final step */}
      {isLast && (
        <div className="space-y-5">
          <div className="space-y-1.5">
            <Label>Engineer notes / recommendations</Label>
            <Textarea
              value={report.data.engineer_notes || ""}
              onChange={(e) => updateData("engineer_notes", e.target.value)}
              placeholder="Summary, advisories, recommendations…"
              data-testid="engineer-notes"
              rows={3}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Follow-up actions</Label>
            <Textarea value={report.follow_up_actions || ""} onChange={(e) => update({ follow_up_actions: e.target.value })} data-testid="follow-up" rows={2} />
          </div>
          <div className="space-y-1.5">
            <Label>Parts required</Label>
            <Input value={report.parts_required || ""} onChange={(e) => update({ parts_required: e.target.value })} className="h-12" data-testid="parts-required" />
          </div>
          <SelectField
            label="Final status"
            value={report.final_status}
            onChange={(v) => update({ final_status: v })}
            options={formDef.finalStatusOptions.map((o) => ({ value: o, label: o }))}
            testid="final-status"
          />

          <div className="space-y-1.5">
            <Label>Photos (faults / condition / evidence)</Label>
            <PhotoUpload photos={report.photos} onChange={(p) => update({ photos: p })} testid="photos" />
          </div>

          <Card className="space-y-5 p-4">
            <SignaturePad label="Engineer signature *" value={report.engineer_signature} onChange={(v) => update({ engineer_signature: v })} testid="engineer-signature" />
            <SignaturePad label="Customer signature (optional)" value={report.customer_signature} onChange={(v) => update({ customer_signature: v })} testid="customer-signature" />
          </Card>
        </div>
      )}

      {/* quick-add / edit dialog (customer / site / heat pump) */}
      <QuickAddDialog
        type={addType}
        title={pumpEditId ? "Edit heat pump" : undefined}
        form={addForm}
        setForm={setAddForm}
        onSave={saveAdd}
        saving={addSaving}
        onClose={() => {
          setAddType(null);
          setAddForm({});
          setPumpEditId(null);
        }}
      />

      <AlertDialog open={removePumpOpen} onOpenChange={setRemovePumpOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this heat pump?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the heat pump asset and any reports linked to
              it, and clears it from this report. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={removePump} className="bg-red-600 hover:bg-red-700" data-testid="confirm-remove-pump">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* sticky footer nav */}
      <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-white p-3 shadow-[0_-2px_10px_rgba(0,0,0,0.06)]">
        <div className="mx-auto flex max-w-5xl items-center gap-2">
          {step > 0 && (
            <Button
              variant="outline"
              className="h-12"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              data-testid="prev-step"
            >
              <ArrowLeft className="mr-1 h-4 w-4" /> Back
            </Button>
          )}
          <Button variant="outline" className="h-12 flex-1" onClick={saveDraft} disabled={saving} data-testid="save-draft">
            {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
            Save draft
          </Button>
          {isLast ? (
            <Button className="h-12 flex-1" onClick={submit} disabled={saving} data-testid="submit-report">
              <Check className="mr-1 h-4 w-4" /> Complete
            </Button>
          ) : (
            <Button
              className="h-12 flex-1"
              onClick={goNext}
              disabled={!canProceed}
              data-testid="next-step"
            >
              Next <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function SectionFields({ section, data, updateData }) {
  const [scanId, setScanId] = useState(null);
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-500">{section.description}</p>
      {section.fields.map((f) => {
        const val = data[f.id];
        if (f.type === "check")
          return (
            <Card key={f.id} className="flex items-center justify-between gap-3 p-4">
              <span className="text-sm font-medium text-slate-700">{f.label}</span>
              <Switch checked={!!val} onCheckedChange={(c) => updateData(f.id, c)} data-testid={`field-${f.id}`} />
            </Card>
          );
        if (f.type === "remarks")
          return (
            <div key={f.id} className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-slate-400">{f.label}</Label>
              <Textarea value={val || ""} onChange={(e) => updateData(f.id, e.target.value)} rows={2} placeholder="Remarks…" data-testid={`field-${f.id}`} />
            </div>
          );
        if (f.type === "select")
          return (
            <div key={f.id} className="space-y-1.5">
              <Label>{f.label}</Label>
              <select
                value={val || ""}
                onChange={(e) => updateData(f.id, e.target.value)}
                data-testid={`field-${f.id}`}
                className="h-12 w-full rounded-md border border-input bg-white px-3 text-sm"
              >
                <option value="">Select…</option>
                {f.options.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
          );
        const scannable = f.type !== "number" && f.id.includes("serial");
        return (
          <div key={f.id} className="space-y-1.5">
            <Label>
              {f.label}
              {f.unit ? <span className="text-slate-400"> ({f.unit})</span> : null}
            </Label>
            <div className="flex gap-2">
              <Input
                type={f.type === "number" ? "number" : "text"}
                inputMode={f.type === "number" ? "decimal" : "text"}
                value={val || ""}
                placeholder={f.placeholder || ""}
                onChange={(e) => updateData(f.id, e.target.value)}
                className="h-12"
                data-testid={`field-${f.id}`}
              />
              {scannable && (
                <Button
                  type="button"
                  variant="outline"
                  className="h-12 shrink-0 px-3"
                  onClick={() => setScanId(f.id)}
                  data-testid={`scan-${f.id}`}
                  title="Scan barcode"
                >
                  <ScanLine className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        );
      })}
      <BarcodeScanner
        open={!!scanId}
        label={scanId ? scanId.replace(/_/g, " ") : "serial number"}
        onDetected={(text) => {
          updateData(scanId, text);
          setScanId(null);
        }}
        onClose={() => setScanId(null)}
      />
    </div>
  );
}

function PumpSummary({ pump }) {
  if (!pump) return null;
  return (
    <Card className="bg-slate-50 p-4" data-testid="pump-summary">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Heat pump details (pre-filled)</p>
      <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        <span className="text-slate-500">Indoor model</span><span className="text-slate-800">{pump.indoor_unit || "—"}</span>
        <span className="text-slate-500">Indoor serial</span><span className="text-slate-800">{pump.indoor_serial_number || "—"}</span>
        <span className="text-slate-500">Outdoor model</span><span className="text-slate-800">{pump.outdoor_unit || "—"}</span>
        <span className="text-slate-500">Serial</span><span className="text-slate-800">{pump.serial_number || "—"}</span>
        <span className="text-slate-500">Refrigerant</span><span className="text-slate-800">{pump.refrigerant_type || "—"}</span>
      </div>
    </Card>
  );
}
